import { topicTimeBreakdown, topicTimeBreakdownProps } from './dataTypes'
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartOptions, Plugin } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useState } from 'react';

ChartJS.register(ArcElement, Tooltip, Legend,ChartDataLabels);


const TopicTimeBreakdown = ({sectionTimeData,module,widgetType} :topicTimeBreakdownProps) => { 

    const [ doughnutKey, setDoughnutKey ] = useState(0);

    const backgroundColors = widgetType === 'topicsTime' ? ["#3B60E4", "#73EEDC", "#FF6542", "#8F3985"] : ["#397367", "#D741A7", "#6610F2", "#FFBA08"];

    const generateData = () => {
      let data: topicTimeBreakdown[] = sectionTimeData || [];
      
      const totalSeconds = data.length > 0 
          ? data.filter((item) => item.contenttype).reduce((sum: number, item: topicTimeBreakdown) => sum + (item.timetaken ?? 0), 0).toFixed(2) 
          : "0";
      
      const totalSkipped = data.length > 0 
          ? data.filter((item) => item.contenttype)
                .reduce((sum: number, item: topicTimeBreakdown) => sum + (item.ommitedquestioncount ?? 0), 0)
                .toFixed(2) 
          : "0";
      let chartData = {
          labels: data.map((item: topicTimeBreakdown) => item.contenttype).filter(Boolean),
          datasets: [
              {
                  data: data.filter((item) => item.contenttype)
                            .map((item: topicTimeBreakdown) => widgetType === 'topicsTime' 
                                ? item.timetaken 
                                : item.ommitedquestioncount),
                  backgroundColor: backgroundColors,
                  borderWidth: 0,
              }
          ]
      }
      const isAllZero = chartData.datasets[0].data.every((value) => value === 0);
      if (isAllZero) {
        chartData.datasets[0].data = [1];
        chartData.datasets[0].backgroundColor = ['#BBBBBD'];
      }
      return {
        chartData,
        totalSeconds,
        totalSkipped,
        isAllZero
      };
  };
    const {chartData,totalSeconds,totalSkipped,isAllZero} = generateData();
  
    const centerTextPlugin: Plugin<"doughnut"> = {
      id: "centerText",
      beforeDraw: (chart) => {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        let totalValue = widgetType === "topicsTime" ? Number(totalSeconds)/60 : Number(totalSkipped);
        totalValue = isNaN(totalValue) ? 0 : Math.round(totalValue);
    
        const text = totalValue.toString();
        const subtext = widgetType === "topicsTime" ? "Mins" : "Skipped";
    
        const x = (chartArea.left + chartArea.right) / 2;
        const y = (chartArea.top + chartArea.bottom) / 2;
    
        ctx.save();
        ctx.font = "bold 28px Gully";
        ctx.fillStyle = "#00356C";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(text, x, y - 15);
        ctx.fillText(subtext, x, y + 15);
        ctx.restore();
      }
    };

    const options: ChartOptions<"doughnut"> = {
        responsive: true,
        plugins: {
            legend: {
              display: false,
              position: "right",
              align: "center",
            },
            tooltip:{
              enabled: true,
              callbacks: {
                label: (tooltipItem) => {
                  const rawValue = tooltipItem.raw as number;
                  if (widgetType === "topicsTime") {
                    const valueInSeconds = rawValue;
                    return `${valueInSeconds.toFixed(2)} secs`;
                  } else {
                    return `${rawValue} questions`;
                  }
                },
              },
              titleFont: {
                family: "Gully",
                size: 14,
                weight: "bold",
              },
              bodyFont: {
                family: "Gully",
                size: 12,
              },
              footerFont: {
                family: "Gully",
                size: 12,
                style: "italic",
              },
            },
            datalabels: {
                display:!isAllZero,
                color: "#fff",
                font: {
                  weight: "bold",
                  size: 14,
                  family: "Gully",
                },
                formatter: (value: number) => {
                  if(widgetType === 'topicsSkipped'){
                      return `${value === 0 ? '' : value}`;
                  } else {
                      const percentage = ((value / Number(totalSeconds)) * 100).toFixed(1);
                      return Number(percentage) > 2 ? `${percentage}%` : '';  
                  }
              }
            }
        }
    };

    const hasValidData = sectionTimeData && sectionTimeData.length > 0;

    useEffect(() => {
      //this is a workaround to force the chart to re-render when the data changes so that centerTextPlugin can update the text
      //this is because the chartjs-doughnut-center-text-plugin does not update the text when the data changes
      setDoughnutKey(Date.now())
    },[sectionTimeData]);

    return (
    <div className="w-full flex flex-col p-3 gap-3 rounded-[18px] border-[0.5px] border-satPrepGrey bg-satPrepTransWhite backdrop-blur-sevenFive shadow-test-card">
        <p className="text-center font-gully text-xl text-satPrepDarkAccent font-medium">
           {widgetType === 'topicsTime' ? 'Topics by time' : 'Topics by skipped questions' }  
        </p>
        <p className="text-center font-gully text-base text-satPrepXDarkAccent font-normal">
            {module}
        </p>

        {hasValidData ? (
          <div className="w-full min-h-[264px] max-h-[300px] flex justify-center items-center gap-5 mx-auto" key={doughnutKey}>
            <Doughnut
              data={chartData}
              options={options}
              plugins={[centerTextPlugin]}
              width={264}
              height={264}
            />
            <div className="space-y-2">
              {sectionTimeData
                ?.filter((item) => item.contenttype)
                .map((item: topicTimeBreakdown) => {
                  return (
                    <div className="flex items-center" key={item.contenttype}>
                      <div
                        className="min-w-[31px] min-h-[31px] rounded-md"
                        style={{
                          backgroundColor: backgroundColors[sectionTimeData.indexOf(item)],
                        }}
                      ></div>
                      <p className="font-gully font-semibold text-sm text-satPrepBlack ml-[6px] text-wrap">
                        {item.contenttype}
                      </p>
                    </div>
                  );
                })}
            </div>
          </div>
        ) : (
          <p className="text-center font-gully text-base text-satPrepXDarkAccent font-normal">
            No data available
          </p>
        )}
    </div>
    )
}

export default TopicTimeBreakdown